import { BrandAllocation } from './../dtos/brand-allocation.models';
import { Injectable } from '@angular/core';

import { Observable } from 'rxjs/Observable';
import { of } from 'rxjs/observable/of';
import { catchError, map, tap } from 'rxjs/operators';

import { ApiResponse, ApiPagedResponse, PaginationInfo } from "app/client-core/api-response-models";
import { ApiService } from "../api.service";
import { Bucket } from '../dtos/buckets.model';
import { Subject } from 'rxjs/Subject';
import { Router } from '@angular/router';
import { BrandListItemDto } from '../dtos/brands.model';
import { RetainerDto } from '../dtos/retainer.model';
import { ContributionDto } from '../dtos/contribution.model';
import { JobsToBeDoneModel } from '../dtos/jobs-to-be-done.model';
import { TypeaheadItemModel } from '../dtos/typeahead-item.model';
import { ChangeDriver, ChangeIndicator } from '../dtos/change-driver.model';
import { NewAllocationRequest } from '../dtos/new-allocation-request.model';
import { NewAllocationResponse } from '../dtos/new-allocation-response.model';

@Injectable()
export class BrandService {
    public AllocationSaved: Subject<any>;
    public AllocationCalceled: Subject<any>;
    public BrandAdded: Subject<any>;

    constructor(private apiService: ApiService,
       private router: Router) {
        this.AllocationSaved = new Subject<any>();
        this.AllocationCalceled = new Subject<any>();
        this.BrandAdded = new Subject<any>();
    }

    /**
     * Returns lookup list of projects
     * @param params
     */
    getBrands(params?: any): Observable<ApiResponse<ApiPagedResponse<BrandListItemDto>>> {
        console.log('>>getBrands', JSON.stringify(params));
        return this.apiService.get<ApiPagedResponse<BrandListItemDto>>('brands', params);
    }

    getBrandsSummary(params?: any): Observable<ApiResponse<ApiPagedResponse<any>>> {
        console.log('>>getBrandsSummary', JSON.stringify(params));
        return this.apiService.get<ApiPagedResponse<BrandListItemDto>>('brands/summary', params);
    }

    getBrandsBudgetYear(params?: any): Observable<ApiResponse<ApiPagedResponse<any>>> {
        console.log('>>getBrandsSummary', JSON.stringify(params));
        return this.apiService.get<ApiPagedResponse<BrandListItemDto>>('brands/budget-year', params);
    }

    getBrandsCategories(params?: any): Observable<ApiResponse<ApiPagedResponse<any>>> {
        console.log('>>getBrandsSummary', JSON.stringify(params));
        return this.apiService.get<ApiPagedResponse<BrandListItemDto>>('brands/categories', params);
    }

    getSectors(id: number, params?: any): Observable<ApiResponse<any>> {
        console.log('>>getSectors', JSON.stringify(params));
        return this.apiService.get<BrandListItemDto>(`brands/${id}/allocation`, params);
    }

    // tslint:disable-next-line: max-line-length
    saveBrandAllocation(
        brandAllocation: BrandAllocation, 
        brandRetailerAllocationId: number,
        changeDriver?: ChangeDriver,
        changeIndicator?: ChangeIndicator
    ): Observable<ApiResponse<ApiPagedResponse<any>>> {
        return this.apiService.put<ApiPagedResponse<any>>(`brands/save-allocation/${brandAllocation.brandId}`,
        {
            brandAllocation,
            brandRetailerAllocationId,
            changeDriver,
            changeIndicator
        });
    }

    getChangeReasons(params?: any): Observable<ApiResponse<ApiPagedResponse<any>>> {
        console.log('>>getSectors', JSON.stringify(params));
        return this.apiService.get<ApiPagedResponse<BrandListItemDto>>(`brands/change-reasons`, params);
    }

    getChangeIndicator(params?: any): Observable<ApiResponse<ChangeIndicator[]>> {
        console.log('>>getSectors', JSON.stringify(params));
        return this.apiService.get<ChangeIndicator[]>(`brands/change-indicators`, params);
    }

    getChangeDriver(params?: any): Observable<ApiResponse<ChangeDriver[]>> {
        return this.apiService.get<ChangeDriver[]>(`brands/change-driver`, params);
    }

    getRetainer(params?: any): Observable<ApiResponse<RetainerDto>> {
        console.log('>>getRetainer', JSON.stringify(params));
        return this.apiService.get<RetainerDto>(`brands/get-retainer`, params);
    }

    saveRetainer(retainer: RetainerDto): Observable<ApiResponse<any>> {
        return this.apiService.put<any>(`brands/save-retainer`, retainer);
    }

    saveBrand(brand: BrandListItemDto): Observable<ApiResponse<any>> {
        return this.apiService.post<any>(`brands`, brand);
    }

    getBrand(id: number): Observable<ApiResponse<BrandListItemDto>> {
        return this.apiService.get<BrandListItemDto>('brands/' + id, {});
    }

    getContributions(budgetYear: number, brandid: number): Observable<ApiResponse<Array<ContributionDto>>> {
        return this.apiService.get<Array<ContributionDto>>(`brands/jobstobedone/${budgetYear}?brandId=${brandid}`, {});
    }

    saveContributions(brandId, contributions: Array<ContributionDto>): Observable<ApiResponse<any>> {
        return this.apiService.put<any>(`brands/jobstobedone/${brandId}`, contributions);
    }

    getCategories(restrictList: boolean = false): Observable<ApiResponse<Array<any>>> {
        return this.apiService.get<Array<any>>(`brands/allcategories`, {
            restrictList
        });
    }

    setFilters(filters: BrandSummaryFilters) {
        console.log(JSON.stringify(filters))
        localStorage.setItem('brandSummaryFilters', JSON.stringify(filters));
    }

    getFilters() {
        const filters = localStorage.getItem('brandSummaryFilters');
        if (filters) {
            return JSON.parse(filters);
        }
        return null;
    }

    getJobsToBeDone(id: number): Observable<ApiResponse<JobsToBeDoneModel[]>> {
        return this.apiService.get<JobsToBeDoneModel[]>(`brands/jobs-to-be-done/${id}`, {});
    }

    saveJobsToBeDone(model: JobsToBeDoneModel): Observable<ApiResponse<boolean>> {
        return this.apiService.post<any>(`brands/jobs-to-be-done`, model);
    }

    deleteJobsToBeDone(id: number): Observable<ApiResponse<boolean>> {
        return this.apiService.delete<any>(`brands/jobs-to-be-done/${id}`);
    }

    deleteJobsToBeDoneBrands(brandid: number, JobsToBeDoneHeaderId): Observable<ApiResponse<boolean>> {
        return this.apiService.delete<any>(`brands/jobstobedone/${brandid}/${JobsToBeDoneHeaderId}`);
    }

    getMoreContributions(params): Observable<ApiResponse<ApiPagedResponse<TypeaheadItemModel>>> {
        let searchTerm = "";
        if (params.searchTerm) {
            searchTerm = `?searchTerm=${params.searchTerm}`
        }
        return this.apiService.get<ApiPagedResponse<TypeaheadItemModel>>(`brands/more-contributions/${params.budgetYear}/${params.brandid}${searchTerm}`, {});
    }

    saveContribution(jobsToBeDoneHeaderId: number, branid: number): Observable<ApiResponse<boolean>> {
        return this.apiService.post<any>(`brands/more-contributions/${jobsToBeDoneHeaderId}/${branid}`, {});
    }

    getNewAllocation(params?: NewAllocationRequest): Observable<ApiResponse<NewAllocationResponse[]>> {
        return this.apiService.post<NewAllocationResponse[]>(`brands/new-allocation-info`, params);
    }
    
}

export interface BrandSummaryFilters {
    budgetYear: number;
    category: number;
    retailer?: number;
}



// WEBPACK FOOTER //
// ./src/app/client-core/services/brand.service.ts